import type { RouteObject } from 'react-router';
import { AccountPage } from '../features/auth/pages/AccountPage';
import { LoginPage, PlatformLoginPage } from '../features/auth/pages/LoginPage';
import {
  AcceptInvitePage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '../features/auth/pages/PasswordPages';
import {
  PlansPage,
  PlatformOverviewPage,
  TenantDetailPage,
  TenantsPage,
} from '../features/platform/pages/PlatformPages';
import { SystemStatusPage } from '../features/system/pages/SystemStatusPage';
import { BranchesPage } from '../features/team/pages/BranchesPage';
import { DepartmentsPage } from '../features/team/pages/DepartmentsPage';
import { RoleDetailPage, RolesListPage } from '../features/team/pages/RolesPages';
import { StaffDetailPage, StaffListPage } from '../features/team/pages/StaffPages';
import { AuditPage } from '../features/tenant/pages/AuditPage';
import { DashboardPage } from '../features/tenant/pages/DashboardPage';
import { OnboardingPage } from '../features/tenant/pages/OnboardingPage';
import { BrandingPage, BusinessSettingsPage } from '../features/tenant/pages/SettingsPages';
import { AppShell } from '../layouts/AppShell';
import { PlatformShell } from '../layouts/PlatformShell';
import { OnboardingGate, RequireAuth, RootRedirect } from './guards';
import { RouteErrorPage } from './RouteErrorPage';

/** Route table. Kept separate from the router instance so tests can mount it in a memory router. */
export const routes: RouteObject[] = [
  { path: '/', element: <RootRedirect />, errorElement: <RouteErrorPage /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/platform/login', element: <PlatformLoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  { path: '/status', element: <SystemStatusPage /> },
  {
    path: '/app',
    element: (
      <RequireAuth type="tenant">
        <OnboardingGate>
          <AppShell />
        </OnboardingGate>
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'onboarding', element: <OnboardingPage /> },
      // Lead screens are split into their own chunks (docs/design/15 §5: route-level code splitting).
      {
        path: 'leads',
        lazy: () => import('../features/leads/pages/LeadsPage').then((m) => ({ Component: m.LeadsPage })),
      },
      {
        path: 'leads/:id',
        lazy: () =>
          import('../features/leads/pages/LeadDetailPage').then((m) => ({ Component: m.LeadDetailPage })),
      },
      {
        path: 'follow-ups',
        lazy: () =>
          import('../features/leads/pages/AssignmentPages').then((m) => ({ Component: m.FollowUpsPage })),
      },
      {
        path: 'assignments',
        lazy: () =>
          import('../features/leads/pages/AssignmentPages').then((m) => ({
            Component: m.AssignmentCentrePage,
          })),
      },
      {
        path: 'settings/leads',
        lazy: () =>
          import('../features/leads/pages/LeadSettingsPage').then((m) => ({ Component: m.LeadSettingsPage })),
      },
      {
        path: 'settings/alerts',
        lazy: () =>
          import('../features/alerts/pages/AlertSettingsPage').then((m) => ({
            Component: m.AlertSettingsPage,
          })),
      },
      {
        path: 'settings/forms',
        lazy: () => import('../features/leads/pages/FormsPage').then((m) => ({ Component: m.FormsPage })),
      },
      { path: 'team/staff', element: <StaffListPage /> },
      { path: 'team/staff/:id', element: <StaffDetailPage /> },
      { path: 'team/branches', element: <BranchesPage /> },
      { path: 'team/departments', element: <DepartmentsPage /> },
      { path: 'team/roles', element: <RolesListPage /> },
      { path: 'team/roles/:id', element: <RoleDetailPage /> },
      { path: 'settings/business', element: <BusinessSettingsPage /> },
      { path: 'settings/branding', element: <BrandingPage /> },
      { path: 'settings/audit', element: <AuditPage /> },
      { path: 'account', element: <AccountPage /> },
    ],
  },
  {
    path: '/platform',
    element: (
      <RequireAuth type="platform">
        <PlatformShell />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <PlatformOverviewPage /> },
      { path: 'tenants', element: <TenantsPage /> },
      { path: 'tenants/:id', element: <TenantDetailPage /> },
      { path: 'plans', element: <PlansPage /> },
      { path: 'account', element: <AccountPage /> },
    ],
  },
  { path: '*', element: <RouteErrorPage /> },
];
